






















import { Component, Mixins, Prop } from "vue-property-decorator";
import Enumerable from "linq";
import {
  NavigationPane,
  IButton,
  IButtonState,
} from "../../../panes";
import {
  IRestriction,
} from "../../../system";
import {
  IRestrictions,
  IRegistrationRestrictions,
} from "../EditingDocument";
import {
  DocumentReferableMixin,
  CommonComponentsAccessibleMixin,
} from "../mixins";
import {
  DocumentAttributes,
  DocumentButtonControl,
} from "../../../../domain/document";
import { IDocumentEditMenuButtonConfiguration, IDocumentEditMenuButtonsConfiguration, IDocumentEditMenuConfiguration } from ".";

interface IButtons {
  register: IButton;
  registerAsTemporary: IButton;
  registerAndPrint: IButton;
  registerAsTemporaryAndPrint: IButton;
  registerAsTemporaryAndPrintWithWatermark: IButton;
  registerAndPrintWithDialog: IButton;
  printWithoutRegistration: IButton;
}

@Component({
  components: {
    NavigationPane,
  },
})
export default class DocumentEditorMenu extends Mixins(
  DocumentReferableMixin,
  CommonComponentsAccessibleMixin,
) {
  @Prop()
  editMenuConfiguration: IDocumentEditMenuConfiguration;
  
  splitLabels(label: string): string[] {
    return label.split("\n");
  }

  data(): any {
    return {
      activeButton: null,
      isPrinting: false,
    };
  }
  // 各種ボタンのクリックのタイミングで、blurイベントでのフォントサイズ自動調整・ダイアログ表示との競合が発生する。
  // ダイアログが表示された場合はボタンからフォーカスが外れるため、それをガードとして利用している。
  activeButton: IButton;
  isPrinting: boolean;

  get buttons(): IButtonState[] {
    return Enumerable.from([
      {
        definition: this._buttons.printWithoutRegistration,
        isVisible: this.isDocumentEditable && this.isDocumentPrintable && this.isPrintableWithoutRegistration,
        isEnable: this.isEditable,
        onClick: () => {
          this.processButtonClicked(
            this._buttons.printWithoutRegistration,
            () => this.printWithoutRegistration());
        },
      },
      {
        definition: this._buttons.registerAsTemporaryAndPrintWithWatermark,
        isVisible: this.isDocumentEditable && this.isDocumentPrintable && this.isWatermarkPrintable && !this.isRegistrationRestricted(s => s.asTemporaryAndPrintingWithWatermark),
        isEnable: this.isEditable,
        onClick: () => {
          this.processButtonClicked(
            this._buttons.registerAsTemporaryAndPrintWithWatermark,
            () => this.commonComponents.operators.registerer.registerDocumentAsTemporaryAndPrint(false, this.shouldValidate, true));
        },
      },
      {
        definition: this._buttons.registerAsTemporaryAndPrint,
        isVisible: this.isDocumentEditable && this.isDocumentPrintable && this.isRegisterAsTemporaryAndPrintVisible() && !this.isRegistrationRestricted(s => s.asTemporaryAndPrinting),
        isEnable: this.isEditable,
        onClick: () => {
          this.processButtonClicked(
            this._buttons.registerAsTemporaryAndPrint,
            () => this.commonComponents.operators.registerer.registerDocumentAsTemporaryAndPrint(false, this.shouldValidate, false));
        },
      },
      {
        definition: this._buttons.registerAsTemporary,
        isVisible: this.isDocumentEditable && this.isRegisterAsTemporaryVisible(!this.isRegistrationRestricted(s => s.asTemporary)),
        isEnable: this.isEditable,
        onClick: () => {
          this.processButtonClicked(
            this._buttons.registerAsTemporary,
            () => this.commonComponents.operators.registerer.registerDocumentAsTemporary(false, this.shouldValidate));
        },
      },
      {
        definition: this._buttons.registerAndPrint,
        isVisible: this.isDocumentEditable && this.isDocumentPrintable && this.isRegisterAndPrintButtonVisible(!this.isRegistrationRestricted(s => s.andPrinting)),
        isEnable: this.isEditable,
        onClick: () => {
          this.processButtonClicked(
            this._buttons.registerAndPrint,
            () => this.commonComponents.operators.registerer.registerDocumentAndPrint(this.shouldValidate, false));
        },
      },
      {
        definition: this._buttons.registerAndPrintWithDialog,
        isVisible: this.isDocumentEditable && this.isDocumentPrintable && this.isRegisterAndPrintWithDialogButtonVisible(!this.isRegistrationRestricted(s => s.andPrintingWithDialog) && this.isPrinterSelectable),
        isEnable: this.isEditable,
        onClick: () => {
          this.processButtonClicked(
            this._buttons.registerAndPrintWithDialog,
            () => this.commonComponents.operators.registerer.registerDocumentAndPrint(this.shouldValidate, true));
        },
      },
      {
        definition: this._buttons.register,
        isVisible: this.isDocumentEditable && this.isRegisterButtonVisible(!this.isRegistrationRestricted(s => s.asNormally)),
        isEnable: this.isEditable,
        onClick: () => {
          this.processButtonClicked(
            this._buttons.register,
            () => this.commonComponents.operators.registerer.registerDocument(this.shouldValidate));
        },
      },
    ])
    .where(b => b.isVisible)
    .toArray();
  }

  private get _buttons(): IButtons {
    return {
      register: {
        name: this.getButtonName(c => c.register, this.$configuration.behavior.document.editor.menu.register.name),
        description: "編集中の文書を登録します。",
        iconClass: "important register",
      },
      registerAsTemporary: {
        name: this.getButtonName(c => c.registerAsTemporary, this.$configuration.behavior.document.editor.menu.registerAsTemporary.name),
        description: "編集中の文書を一時保存します。(登録はされません。)",
        iconClass: "register-as-temporary",
      },
      registerAndPrint: {
        name: this.getButtonName(c => c.registerAndPrint, this.$configuration.behavior.document.editor.menu.registerAndPrint.name),
        description: "編集中の文書を登録してから既定部数印刷します。",
        iconClass: "important register-and-print",
      },
      registerAsTemporaryAndPrint: {
        name: this.getButtonName(c => c.registerAsTemporaryAndPrint, this.$configuration.behavior.document.editor.menu.registerAsTemporaryAndPrint.name),
        description: "編集中の文書を一時保存してから既定部数印刷します。(登録はされません。)",
        iconClass: "register-as-temporary-and-print",
      },
      registerAsTemporaryAndPrintWithWatermark: {
        name: this.getButtonName(c => c.registerAsTemporaryAndPrintWithWatermark, this.$configuration.behavior.document.editor.menu.registerAsTemporaryAndPrintWithWatermark.name),
        description: "編集中の文書を一時保存してから既定部数印刷します。(登録はされません。)",
        iconClass: "register-as-temporary-and-print-with-water-mark",
      },
      registerAndPrintWithDialog: {
        name: this.getButtonName(c => c.registerAndPrintWithDialog, this.$configuration.behavior.document.editor.menu.registerAndPrintWithDialog.name),
        description: "編集中の文書を登録してから出力先を選んで印刷します。",
        iconClass: "important register-and-print-with-dialog",
      },
      printWithoutRegistration: {
        name: this.$configuration.behavior.document.editor.menu.printWithoutRegistration.name,
        description: "編集中の文書を登録しないで既定部数印刷します。",
        iconClass: "print",
      }
    };
  }

  private getButtonName(accessor: (c: IDocumentEditMenuButtonsConfiguration) => IDocumentEditMenuButtonConfiguration, defaultName: string): string {
    const buttonConfiguration = (this.editMenuConfiguration && this.editMenuConfiguration.buttons)
      ? accessor(this.editMenuConfiguration.buttons)
      : null;
    return (buttonConfiguration && buttonConfiguration.name)
      ? buttonConfiguration.name
      : defaultName;
  }

  private isRegistrationRestricted(read: (s: IRegistrationRestrictions) => IRestriction): boolean {
    return this.registrationRestrictions
        && read(this.registrationRestrictions)
        && read(this.registrationRestrictions).isDisabled;
  }

  get registrationRestrictions(): IRegistrationRestrictions {
    return (this.restrictions)
      ? this.document.restrictions.registration
      : null;
  }

  private isRestricted(read: (s: IRestrictions) => IRestriction): boolean {
    return this.restrictions
        && read(this.restrictions)
        && read(this.restrictions).isDisabled;
  }

  get restrictions(): IRestrictions {
    return this.document
      ? this.document.restrictions
      : null;
  }

  get shouldValidate(): boolean {
    return !this.isRestricted(s => s.validation);
  }

  get isEditable(): boolean {
    return this.readPropertyOrDefault(s => s.isAlreadyInitialized, false);
  }

  get isDocumentEditable(): boolean {
    return this.$store.getters.userAuthority.documentEditable;
  }

  get isDocumentPrintable(): boolean {
    return this.$store.getters.userAuthority.documentPrintable;
  }

  get isWatermarkPrintable(): boolean {
    return this.$configuration.behavior.document.editor.menu.registerAsTemporaryAndPrintWithWatermark.isEnable;
  }

  get isPrinterSelectable(): boolean {
    return this.$configuration.behavior.document.editor.menu.registerAndPrintWithDialog.isEnable;
  }

  get shouldIndicatePrintButtonForcibly(): boolean {
    return this.attributes && this.attributes.behavior && this.attributes.behavior.shouldIndicatePrintButtonForcibly;
  }

  get isPrintOnlyButtonControl(): boolean {
    // enableButtonControl=falseの場合はthis.buttonControlがnullとなるので、ワークフローではtrueになることはない見込み
    return  !!this.buttonControl
      && (this.buttonControl.isRegisterEnable === false)
      && (this.buttonControl.isRegisterAndPrintEnable === false)
      && (this.buttonControl.isRegisterAndPrintWithDialogEnable === false)
      && (this.buttonControl.isRegisterAsTemporaryEnable === false)
      && (this.buttonControl.isRegisterAsTemporaryAndPrintEnable === false)
      && this.buttonControl.isPrintEnable;
  }

  get isPrintableWithoutRegistration(): boolean {
    return this.shouldIndicatePrintButtonForcibly || this.isPrintOnlyButtonControl;
  }

  onBlur(button: IButton): void {
    if (this.activeButton === button) {
      this.activeButton = null;
    }
  }

  processButtonClicked(button: IButton, process: () => void): void {
    this.activeButton = button;
    this.$nextTick(() => {
      if (this.activeButton === button) {
        process();
      }
    });
  }

  get attributes(): DocumentAttributes {
    return !!this.document
      ? this.document.attributes
      : null;
  }

  get buttonControl(): DocumentButtonControl {
    return (!!this.attributes && !!this.attributes.behavior && this.attributes.behavior.enableButtonControl && !!this.attributes.buttonControl)
      ? this.attributes.buttonControl
      : null;
  }

  isRegisterButtonVisible(defaultValue: boolean): boolean {
    return !!this.buttonControl && (this.buttonControl.isRegisterEnable !== null)
      ? this.buttonControl.isRegisterEnable
      : defaultValue;
  }

  isRegisterAndPrintButtonVisible(defaultValue: boolean): boolean {
    return !!this.buttonControl && (this.buttonControl.isRegisterAndPrintEnable !== null)
      ? this.buttonControl.isRegisterAndPrintEnable
      : defaultValue;
  }

  isRegisterAndPrintWithDialogButtonVisible(defaultValue: boolean): boolean {
    return !!this.buttonControl && (this.buttonControl.isRegisterAndPrintWithDialogEnable !== null)
      ? this.buttonControl.isRegisterAndPrintWithDialogEnable
      : defaultValue;
  }

  isRegisterAsTemporaryVisible(defaultValue: boolean): boolean {
    return !!this.buttonControl && (this.buttonControl.isRegisterAsTemporaryEnable !== null)
      ? this.buttonControl.isRegisterAsTemporaryEnable
      : defaultValue;
  }

  isRegisterAsTemporaryAndPrintVisible(): boolean {
    // 一時保存・印刷はデフォルトでは出てほしくないので他と判定を少し変えている
    return !!this.buttonControl && (this.buttonControl.isRegisterAsTemporaryAndPrintEnable === true);
  }

  private printWithoutRegistration(): void {
    this.isPrinting = true;
    this.commonComponents.operators.printer.printAsync(false, true, false)
    .then(() => {
      const configuration = this.$configuration.dangerZone.document.editor.printingWithoutRegistration;
      if (this.shouldIndicatePrintButtonForcibly && configuration.shouldCloseWindowWhenForciblyEnabled) {
        // 群馬大のV1は特別対応が入っていて、印刷ボタンを押したら閉じる挙動になっている
        // ・群馬大のV1
        // ・設定がある場合のみ（現地では端末設定）
        // ・起動パラメータに「登録後自動終了」がある＋直接記載＋印刷のみの場合
        // 完全に同じ条件にはならないが、印刷ボタンを強制表示しているときだけ自動的に閉じる機能を追加することで対応している
        this.$info("画面自動クローズ", (configuration.closeWaitTimeAsMsec / 1000) + "秒後に自動的に画面を閉じます。");
        this.$sleep(configuration.closeWaitTimeAsMsec)
        .then(() => {
          // ぐるぐるは解除しないでそのまま閉じる
          this.$closeWindow(true);
        });
      } else {
        // ぐるぐるを解除して編集画面に戻る
        this.$success("印刷", "編集中の文書を印刷します。");
        this.isPrinting = false;
      }
    }).catch(e => {
      this.$error("印刷エラー", "印刷処理中にエラーが発生しました。", e);
      this.isPrinting = false;
    });
  }
}
